/**
 * Boards on /tasks: statuses, keys, label tones and the per-board view
 * preferences. Pure helpers only, so node tests can load them.
 *
 * Contract: docs/boards-api.md in Gluska-Forge-API. `status` on a task keeps
 * its legacy meaning (open / in_progress / completed); the canonical status
 * is `state`. Everything here reads `state` and falls back to `status` for a
 * task serialised before boards existed (an old cached list, say).
 */

import type { Task } from "@/types/task.types"
import { dayIn } from "./task-due"

export type TaskState = "triage" | "backlog" | "todo" | "in_progress" | "done" | "canceled"

/** Who sees a board (docs/boards-api.md §12): the usual rule, or its member list only (admins included). */
export type BoardVisibility = "workspace" | "members"

/** A member's role on a board, lowest first. */
export type BoardRole = "viewer" | "editor" | "manager" | "owner"
export const BOARD_ROLES: BoardRole[] = ["viewer", "editor", "manager", "owner"]

export const BOARD_ROLE_LABELS: Record<BoardRole, string> = {
  viewer: "Viewer",
  editor: "Editor",
  manager: "Manager",
  owner: "Owner",
}

export const BOARD_ROLE_HINTS: Record<BoardRole, string> = {
  viewer: "Reads tasks only",
  editor: "Creates and edits tasks",
  manager: "Also manages members and settings",
  owner: "Everything, including visibility and deleting the board",
}

export function boardRoleLevel(role: BoardRole | null | undefined): number {
  return role ? BOARD_ROLES.indexOf(role) + 1 : 0
}

export interface BoardMember {
  id: number
  name: string
  email: string
  avatar: string | null
  role: BoardRole
}

export interface Board {
  id: number
  key: string
  name: string
  color: string | null
  is_default: boolean
  visibility?: BoardVisibility
  /** The caller's own role; `owner` for an admin on a workspace board, null on General. */
  role?: BoardRole | null
  can_manage?: boolean
  can_own?: boolean
  grantable_roles?: BoardRole[]
  members?: BoardMember[]
  is_member?: boolean
  member_count?: number
  open_count?: number
  next_number?: number
  statuses?: TaskState[]
  /** FOR-349: an archived board is out of the default list and its tasks are read-only. */
  archived?: boolean
  archived_at?: string | null
  /** GET /boards/{key} only: archive and unarchive, newest first (at most 20). */
  activity?: BoardActivityEntry[]
}

export interface BoardActivityEntry {
  action: "archived" | "unarchived"
  user: { id: number; name: string } | null
  created_at: string
}

// ── Archived boards (FOR-349) ──────────────────────────────────────────────

export const BOARD_ARCHIVED_MESSAGE = "This board is archived, so its tasks are read-only."

export function isBoardArchived(board: { archived?: boolean; archived_at?: string | null } | null | undefined): boolean {
  return !!board && (board.archived === true || !!board.archived_at)
}

/** The API's 403 for a write on an archived board's task: `{ error, code: "board_archived" }`. */
export function isBoardArchivedError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response
  return response?.data?.code === "board_archived"
}

export const GENERAL_BOARD_KEY = "GEN"

export const TASK_STATE_LABELS: Record<TaskState, string> = {
  triage: "Triage",
  backlog: "Backlog",
  todo: "Todo",
  in_progress: "In Progress",
  done: "Done",
  canceled: "Canceled",
}

// Picker order: the lifecycle a task walks through.
export const TASK_STATE_ORDER: TaskState[] = ["triage", "backlog", "todo", "in_progress", "done", "canceled"]

// List order when grouped by status: what needs a decision first, then work
// under way, then what is queued, with the closed groups last (and collapsed).
export const TASK_STATE_GROUP_ORDER: TaskState[] = ["triage", "in_progress", "todo", "backlog", "done", "canceled"]

// General has no Triage (docs/boards-api.md §3).
export const GENERAL_STATES: TaskState[] = ["backlog", "todo", "in_progress", "done", "canceled"]

export function isTaskState(value: unknown): value is TaskState {
  return typeof value === "string" && (TASK_STATE_ORDER as string[]).includes(value)
}

export function taskState(task: { status?: string | null; state?: string | null }): TaskState {
  if (isTaskState(task.state)) return task.state
  if (task.status === "completed") return "done"
  if (task.status === "in_progress") return "in_progress"
  return "todo"
}

export function isClosedState(state: TaskState): boolean {
  return state === "done" || state === "canceled"
}

export function legacyStatus(state: TaskState): Task["status"] {
  if (isClosedState(state)) return "completed"
  return state === "in_progress" ? "in_progress" : "open"
}

/** The optimistic patch for a status change: both fields move together. */
export function statePatch(state: TaskState): { state: TaskState; status: Task["status"] } {
  return { state, status: legacyStatus(state) }
}

export function boardStates(board: Pick<Board, "statuses" | "is_default"> | null | undefined): TaskState[] {
  const listed = (board?.statuses ?? []).filter(isTaskState)
  if (listed.length > 0) return TASK_STATE_ORDER.filter((s) => listed.includes(s))
  return board && !board.is_default ? TASK_STATE_ORDER : GENERAL_STATES
}

/** The boards someone belongs to; General always counts. */
export function memberBoards(boards: Board[]): Board[] {
  return boards.filter((b) => b.is_default || b.is_member)
}

// ── Priority ───────────────────────────────────────────────────────────────

const PRIORITY_LABELS: Record<string, string> = {
  urgent: "Urgent",
  high: "High",
  medium: "Medium",
  low: "Low",
}

export function priorityLabel(priority: string): string {
  return PRIORITY_LABELS[priority] ?? priority
}

// ── Keys and URLs ───────────────────────────────────────────────────────────

/** What goes in /tasks/{ref}: the key (GEN-12, FOR-279); the id only for a row without one. */
export function taskRef(task: { id: number; key?: string | null }): string {
  return task.key ? task.key : String(task.id)
}

/** What a person reads: GEN-12, FOR-279; #1442 only for a row without a key. */
export function taskDisplayKey(task: { id: number; key?: string | null }): string {
  return task.key ? task.key : `#${task.id}`
}

/** A route param is a task reference when it is a numeric id or a key. */
export function isTaskRefParam(param: string | undefined): param is string {
  return !!param && (/^\d+$/.test(param) || /^[A-Za-z][A-Za-z0-9]{0,9}-\d+$/.test(param))
}

/** Normalises ?board= to an upper-case key, General when absent. */
export function boardKeyFromParam(param: string | null | undefined): string {
  const key = (param ?? "").trim().toUpperCase()
  return /^[A-Z][A-Z0-9]{0,9}$/.test(key) ? key : GENERAL_BOARD_KEY
}

/** Matches search text against the title, the key (FOR-12) and #id. */
export function taskMatchesSearch(task: { id: number; title: string; key?: string | null }, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  if (task.title.toLowerCase().includes(q)) return true
  const bare = q.replace(/^#/, "")
  if (task.key && task.key.toLowerCase() === bare) return true
  return /^\d+$/.test(bare) && String(task.id) === bare
}

// ── Per-board view preferences ─────────────────────────────────────────────

export type TaskGroupMode = "section" | "project" | "status"
export type TaskDensity = "comfortable" | "compact"
export type TaskStatusFilter = "open" | "completed" | "all"

// General keeps the storage keys it has always used, so nobody's saved view
// changes when boards ship. Product boards get their own, suffixed by key.
function prefKey(base: string, boardKey: string): string {
  return boardKey === GENERAL_BOARD_KEY ? base : `${base}:${boardKey}`
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">

function read(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    // Private windows can refuse storage outright; the defaults apply.
    return null
  }
}

function write(storage: Storage | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value)
  } catch {
    // Same as read: an unwritable store only costs the remembered view.
  }
}

export function readGroupMode(boardKey: string, storage?: Storage): TaskGroupMode {
  const isGeneral = boardKey === GENERAL_BOARD_KEY
  const saved = read(storage, prefKey("tasks-group-mode", boardKey))
  // Sections belong to General; a product board never lands on them.
  if (saved === "project" || saved === "status") return saved
  if (saved === "section" && isGeneral) return "section"
  return isGeneral ? "section" : "status"
}

export function saveGroupMode(boardKey: string, mode: TaskGroupMode, storage?: Storage) {
  write(storage, prefKey("tasks-group-mode", boardKey), mode)
}

export function readDensity(boardKey: string, storage?: Storage): TaskDensity {
  const saved = read(storage, prefKey("tasks-density", boardKey))
  if (saved === "compact" || saved === "comfortable") return saved
  return boardKey === GENERAL_BOARD_KEY ? "comfortable" : "compact"
}

export function saveDensity(boardKey: string, density: TaskDensity, storage?: Storage) {
  write(storage, prefKey("tasks-density", boardKey), density)
}

// Product boards list every status by default, because their Done group sits
// collapsed at the bottom the way Linear shows it. General keeps Open.
export function readStatusFilter(boardKey: string, storage?: Storage): TaskStatusFilter {
  const saved = read(storage, prefKey("tasks-status-filter", boardKey))
  if (saved === "open" || saved === "completed" || saved === "all") return saved
  return boardKey === GENERAL_BOARD_KEY ? "open" : "all"
}

export function saveStatusFilter(boardKey: string, filter: TaskStatusFilter, storage?: Storage) {
  write(storage, prefKey("tasks-status-filter", boardKey), filter)
}

// ── Labels ─────────────────────────────────────────────────────────────────

/**
 * The six badge tones from resend-styles (04-badges-pills). A label's own
 * colour picks the nearest tone by hue, so every label reads in the one badge
 * style in both themes instead of carrying an arbitrary hex.
 */
export type BadgeTone = "success" | "info" | "warning" | "error" | "violet" | "neutral"

export function labelTone(color: string | null | undefined): BadgeTone {
  const hsl = hexToHsl(color)
  if (!hsl) return "neutral"
  const [h, s, l] = hsl
  if (s < 0.18 || l < 0.12 || l > 0.92) return "neutral"
  if (h < 18 || h >= 340) return "error"
  if (h < 62) return "warning"
  if (h < 170) return "success"
  if (h < 255) return "info"
  return "violet"
}

function hexToHsl(color: string | null | undefined): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color ?? "").trim())
  if (!m) return null
  const hex = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1]
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return [0, 0, l]
  const s = d / (1 - Math.abs(2 * l - 1))
  let h: number
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  h *= 60
  if (h < 0) h += 360
  return [h, s, l]
}

// ── Due dates ──────────────────────────────────────────────────────────────

export type DueTone = "late" | "soon" | "normal"

/**
 * Red when due today or overdue, amber when due within the next two days.
 * Closed tasks never warn. `today` is a local yyyy-mm-dd string.
 *
 * A task due at a time (`dueAt`, FOR-302) turns red when that time passes and
 * is amber earlier on its day; its day is the local date of the time.
 */
export function dueTone(
  dueDate: string | null | undefined,
  today: string,
  closed = false,
  dueAt: string | null | undefined = null,
  now: Date = new Date(),
): DueTone | null {
  const at = dueAt ? Date.parse(dueAt) : NaN
  if (!dueDate && Number.isNaN(at)) return null
  if (closed) return "normal"
  if (!Number.isNaN(at)) {
    if (at < now.getTime()) return "late"
    const day = dayIn(new Date(at))
    if (day <= today) return "soon"
    dueDate = day
  }
  const due = (dueDate as string).slice(0, 10)
  if (due <= today) return "late"
  const days = (Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000
  return days <= 2 ? "soon" : "normal"
}

// ── Reported via ───────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  slack: "Slack",
  slack_thread: "Slack thread",
  manageph: "ManagePH",
  quick_add: "Quick add",
  "agent-run": "Agent run",
  agent_run: "Agent run",
  feedback: "In-app feedback",
  in_app_feedback: "In-app feedback",
  app_feedback: "In-app feedback",
  linear: "Linear",
  cli: "Forge CLI",
  "forge-cli": "Forge CLI",
  forge_cli: "Forge CLI",
  mcp: "Forge MCP",
}

// Board apps whose ids do not read as their names. Any other app is named after
// its board when the two match ("vela" on Vela), else tidied.
const APP_LABELS: Record<string, string> = {
  "forge-feedback": "Forge feedback",
  speechtherapymagic: "SpeechTherapyMagic",
  vela: "Vela",
}

function tidied(text: string): string {
  return text.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase())
}

const squashed = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "")

/**
 * The name of a board app (`external_ref.app` on a task it filed, `app` on a
 * comment it posted). The same mapping as forge-ios `TaskReportedVia.appLabel`.
 */
export function boardAppLabel(app: string | null | undefined, boardName?: string | null): string {
  const id = app?.trim()
  if (!id) return "Connected app"
  const known = APP_LABELS[id.toLowerCase()]
  if (known) return known
  if (boardName && squashed(boardName) === squashed(id)) return boardName
  return tidied(id)
}

/**
 * A readable name for `external_source`, or null for tasks made in Forge. A
 * task an app filed through the boards intake is `board_app` with the app in
 * `external_ref.app`, and it is the app worth naming: "Board app" says nothing.
 */
export function reportedViaLabel(
  source: string | null | undefined,
  app?: string | null,
  boardName?: string | null,
): string | null {
  const key = source?.trim()
  if (!key) return null
  if (key.toLowerCase() === "board_app") return boardAppLabel(app, boardName)
  return SOURCE_LABELS[key.toLowerCase()] ?? tidied(key)
}

/**
 * Where the original lives, when the task links back to it: the Linear issue
 * it was imported from, or the Slack message it was filed from. https only.
 */
export function reportedViaLink(
  source: string | null | undefined,
  ref: Record<string, unknown> | null | undefined,
): { url: string; label: string } | null {
  const https = (value: unknown) => (typeof value === "string" && /^https:\/\/[^/\s]+/i.test(value) ? value : null)
  switch (source?.trim().toLowerCase()) {
    case "linear": {
      const url = https(ref?.url)
      return url ? { url, label: "Open in Linear" } : null
    }
    case "slack":
    case "slack_thread": {
      const url = https(ref?.permalink)
      return url ? { url, label: "Open in Slack" } : null
    }
    default:
      return null
  }
}

/**
 * Who wrote a comment. A comment a board app posted is stored under the
 * teammate the app's token acts as, so `user` names that teammate; the author
 * is the app's own user in `external_author`, shown "<name> via <App>".
 */
export function commentAuthor(
  comment: { app?: string | null; external_author?: { name?: string | null; email?: string | null } | null; user?: { name: string } | null },
  boardName?: string | null,
): { name: string; via: string | null } {
  const app = comment.app?.trim()
  if (!app) return { name: comment.user?.name ?? "Forge teammate", via: null }
  const author = [comment.external_author?.name, comment.external_author?.email]
    .map((part) => part?.trim())
    .find((part) => part)
  return { name: author ?? boardAppLabel(app, boardName), via: `via ${boardAppLabel(app, boardName)}` }
}
